import { Reducer } from 'redux';
import { ActionType, getType } from 'typesafe-actions';
import { IGameState } from '../Game/IGameState';
import { purchaseLife } from './Action/PurchaseLife';
import { receiveBoosterBullsEye } from './Action/ReceiveBoosterBullsEye';
import { receiveBoosterJoker } from './Action/ReceiveBoosterJoker';
import { receiveBoosterShuffle } from './Action/ReceiveBoosterShuffle';
import { receiveBoosterTNT } from './Action/ReceiveBoosterTNT';
import { receiveBoosterUndo } from './Action/ReceiveBoosterUndo';
import { receiveCoins } from './Action/ReceiveCoins';
import { receiveExtraCards } from './Action/ReceiveExtraCards';
import { receiveFoxReward } from './Action/ReceiveFoxReward';
import { receiveKeys } from './Action/ReceiveKeys';
import { receiveLives } from './Action/ReceiveLives';
import { receiveMissionReward } from './Action/ReceiveMissionReward';
import { selectBackCard } from './Action/SelectBackcard';
import { setMusicVolume } from './Action/SetMusicVolume';
import { setPlayerState } from './Action/SetPlayerState';
import { setSoundVolume } from './Action/SetSoundVolume';
import { unlockChest } from './Action/UnlockChest';
import { unlockPostcard } from './Action/UnlockPostcard';
import { agreeWithTerms } from './Action/GDPRCompliance';
import { PlayerActions } from './PlayerActions';
import {
    seeNewBackcards,
    unlockBackCard,
    unlockNextBackCard,
} from './PlayerBackcardHelper';
import { updateMissionProgress } from './PlayerMissionHelper';
import { clearTasks } from './PlayerStateHelper';
import { updateTimers } from './PlayerTimerHelper';
import { setWheelSpins, spinWheel } from './PlayerWheelHelper';
import { setFlag } from './PlayerFlagHelper';
import { APP_RATE_SUBMITTED } from '../../Constants/Flags';
import { toggleNotification } from './Action/PushNotification';

export const PlayerReducer: Reducer<IGameState> = (
    state: IGameState,
    action: ActionType<typeof PlayerActions>
): IGameState => {
    switch (action.type) {
        case getType(PlayerActions.setPlayerState):
            return setPlayerState(state, action.payload);
        case getType(PlayerActions.purchaseLife):
            return purchaseLife(state);
        case getType(PlayerActions.receiveCoins):
            return receiveCoins(state, action.payload);
        case getType(PlayerActions.receiveLives):
            return receiveLives(state, action.payload);
        case getType(PlayerActions.receiveKeys):
            return receiveKeys(state, action.payload);
        case getType(PlayerActions.updateTimers):
            return { ...state, playerState: updateTimers(state.playerState) };
        case getType(PlayerActions.receiveBoosterTNT):
            return receiveBoosterTNT(state, action.payload);
        case getType(PlayerActions.receiveBoosterBullsEye):
            return receiveBoosterBullsEye(state, action.payload);
        case getType(PlayerActions.receiveBoosterShuffle):
            return receiveBoosterShuffle(state, action.payload);
        case getType(PlayerActions.receiveBoosterUndo):
            return receiveBoosterUndo(state, action.payload);
        case getType(PlayerActions.receiveBoosterJoker):
            return receiveBoosterJoker(state, action.payload);
        case getType(PlayerActions.receiveExtraCards):
            return receiveExtraCards(state, action.payload);
        case getType(PlayerActions.selectBackCard):
            return selectBackCard(state, action.payload);
        case getType(PlayerActions.unlockBackCard):
            if (action.payload) {
                return unlockBackCard(state, action.payload);
            }
            return unlockNextBackCard(state);
        case getType(PlayerActions.seeNewBackcards):
            return {
                ...state,
                playerState: seeNewBackcards(state.playerState),
            };
        case getType(PlayerActions.setMissionProgress):
            return {
                ...state,
                playerState: updateMissionProgress(
                    state.playerState,
                    state.playerState.missions[action.payload.mission].type,
                    action.payload.progress
                ),
            };
        case getType(PlayerActions.receiveMissionReward):
            return receiveMissionReward(state, action.payload);
        case getType(PlayerActions.receiveWheelSpins):
            return {
                ...state,
                playerState: setWheelSpins(
                    state.playerState,
                    state.playerState.wheelSpins + action.payload
                ),
            };
        case getType(PlayerActions.spinWheel):
            return spinWheel(state);
        case getType(PlayerActions.receiveFoxReward):
            return receiveFoxReward(state);
        case getType(PlayerActions.clearTasks):
            return {
                ...state,
                playerState: clearTasks(state.playerState),
            };
        case getType(PlayerActions.setVolumeSFX):
            return setSoundVolume(state, action.payload);
        case getType(PlayerActions.setVolumeMusic):
            return setMusicVolume(state, action.payload);
        case getType(PlayerActions.unlockChest):
            return unlockChest(state, action.payload);
        case getType(PlayerActions.unlockPostcard):
            return unlockPostcard(state, action.payload);
        case getType(PlayerActions.claimReward):
            return {
                ...state,
                playerState: {
                    ...state.playerState,
                    tasks: [
                        ...state.playerState.tasks,
                        ...state.playerState.spinWheelTask,
                    ],
                    spinWheelTask: [],
                },
            };
        case getType(PlayerActions.submitRate):
            return {
                ...state,
                playerState: setFlag(state.playerState, APP_RATE_SUBMITTED),
            };
        case getType(PlayerActions.agreeWithTerms):
            return agreeWithTerms(state);
        case getType(PlayerActions.toggleNotifications):
            return toggleNotification(state, action.payload);
    }

    return state;
};
